import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Header/Navbar';
import Footer from '../components/Footer';
import Button from '../components/Button';
import SbPageHeader from '../components/layout/SbPageHeader';
import SbEmptyState from '../components/layout/SbEmptyState';
import { useNotifications, type Notification } from '../contexts/NotificationContext';
import { getNotificationMeta, NotificationTypeId } from '../utils/notificationMeta';
import RatingModal from '../components/Rating/RatingModal';
import { usePolicyAcceptanceFromNotification } from '../hooks/usePolicyAcceptanceFromNotification';
import '@fortawesome/fontawesome-free/css/all.min.css';

type FilterKey = 'all' | 'unread' | number;

const FILTER_TABS: { key: FilterKey; label: string; icon: string }[] = [
  { key: 'all', label: 'Tümü', icon: 'list' },
  { key: 'unread', label: 'Okunmamış', icon: 'envelope' },
  { key: NotificationTypeId.PriceDrop, label: 'Fiyat Değişimleri', icon: 'tag' },
  { key: NotificationTypeId.ListingApproved, label: 'Onaylanan İlanlar', icon: 'check-circle' },
  { key: NotificationTypeId.NewMessage, label: 'Mesajlar', icon: 'envelope-open-text' },
];

const EXTRA_FILTERS: { key: FilterKey; label: string; icon: string; btnClass: string }[] = [
  { key: NotificationTypeId.ListingRejected, label: 'Reddedilen İlanlar', icon: 'times-circle', btnClass: 'danger' },
  { key: NotificationTypeId.SupportTicketResolved, label: 'Destek Sonuçları', icon: 'headset', btnClass: 'info' },
  { key: NotificationTypeId.AccountWarning, label: 'Uyarılar', icon: 'exclamation-triangle', btnClass: 'warning' },
  { key: NotificationTypeId.RuleUpdate, label: 'Kural Güncellemeleri', icon: 'file-contract', btnClass: 'secondary' },
  { key: NotificationTypeId.RatingRequest, label: 'Değerlendirme', icon: 'star', btnClass: 'warning' },
];

function navigateByType(typeId: number, navigate: ReturnType<typeof useNavigate>) {
  switch (typeId) {
    case NotificationTypeId.NewMessage:
      navigate('/mesajlar');
      break;
    case NotificationTypeId.PriceDrop:
      navigate('/favoriler');
      break;
    case NotificationTypeId.ListingApproved:
    case NotificationTypeId.ListingRejected:
      navigate('/ilanlarim');
      break;
    case NotificationTypeId.SupportTicketResolved:
      navigate('/teknik-destek');
      break;
    case NotificationTypeId.AccountWarning:
      navigate('/profil');
      break;
    case NotificationTypeId.RuleUpdate:
      break;
    default:
      break;
  }
}

const Notifications: React.FC = () => {
  const {
    notifications,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
  } = useNotifications();

  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState<FilterKey>('all');
  const [ratingTarget, setRatingTarget] = useState<{ notificationId: string; soldListingId: string; listingTitle: string } | null>(null);
  const { handleNotificationClick: handlePolicyNotificationClick } = usePolicyAcceptanceFromNotification();

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const filteredNotifications =
    activeFilter === 'all'
      ? notifications
      : activeFilter === 'unread'
        ? notifications.filter((n) => !n.isRead)
        : notifications.filter((n) => n.typeId === activeFilter);

  const countFor = (key: FilterKey): number => {
    if (key === 'all') return notifications.length;
    if (key === 'unread') return notifications.filter((n) => !n.isRead).length;
    return notifications.filter((n) => n.typeId === key).length;
  };

  const handleNotificationClick = (notification: Notification) => {
    // RatingRequest opens the in-app rating modal rather than navigating away —
    // markAsRead is deferred until the rating is successfully submitted so the
    // buyer can still see the CTA if they dismiss the modal.
    if (notification.typeId === NotificationTypeId.RatingRequest) {
      const soldListingId = notification.contextData?.soldListingId;
      const listingTitle = notification.contextData?.listing ?? notification.title ?? '';
      if (soldListingId) {
        setRatingTarget({ notificationId: notification.id, soldListingId, listingTitle });
        return;
      }
    }

    if (notification.typeId === NotificationTypeId.RuleUpdate) {
      const opened = handlePolicyNotificationClick(notification, () => markAsRead(notification.id));
      if (opened) return;
    }

    if (!notification.isRead) {
      markAsRead(notification.id);
    }
    navigateByType(notification.typeId, navigate);
  };

  const formatDate = (dateStr?: string | null): string => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMin = Math.floor(diffMs / 60000);
      if (diffMin < 1) return 'Şimdi';
      if (diffMin < 60) return `${diffMin} dakika önce`;
      const diffHours = Math.floor(diffMin / 60);
      if (diffHours < 24) return `${diffHours} saat önce`;
      const diffDays = Math.floor(diffHours / 24);
      if (diffDays < 7) return `${diffDays} gün önce`;
      return date.toLocaleDateString('tr-TR');
    } catch {
      return '';
    }
  };

  return (
    <div className="min-vh-100" style={{ backgroundColor: 'var(--sb-bg-muted)' }}>
      <Navbar />

      <main className="sb-page-container py-4 sb-page-shell">
        <SbPageHeader
          title="Bildirimler"
          subtitle="Hesabınızla ilgili güncellemeleri buradan takip edin."
          backTo={{ to: '/', label: 'Ana Sayfaya Dön' }}
          actions={
            <Button
              className="btn btn-link text-primary text-decoration-none p-0"
              onClick={markAllAsRead}
              disabled={notifications.every((n) => n.isRead)}
            >
              Tümünü Okundu İşaretle
            </Button>
          }
        />

        <div className="sb-notification-filters">
          {[...FILTER_TABS, ...EXTRA_FILTERS].map((tab) => (
            <button
              key={String(tab.key)}
              type="button"
              className={`sb-filter-chip ${activeFilter === tab.key ? 'sb-filter-chip--active' : ''}`}
              onClick={() => setActiveFilter(tab.key)}
            >
              {tab.label}
              {countFor(tab.key) > 0 ? ` (${countFor(tab.key)})` : ''}
            </button>
          ))}
        </div>

        <div className="sb-panel-card">
          <div className="p-0">
            {loading && notifications.length === 0 ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Yükleniyor...</span>
                </div>
              </div>
            ) : filteredNotifications.length > 0 ? (
              <div className="list-group list-group-flush">
                {filteredNotifications.map((notification) => {
                  const meta = getNotificationMeta(notification.typeId);
                  return (
                    <button
                      key={notification.id}
                      type="button"
                      className={`list-group-item list-group-item-action p-3 border-0 text-start ${!notification.isRead ? 'sb-surface-muted' : ''}`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="d-flex">
                        <div
                          className={`${meta.bgColor} text-white rounded-circle me-3 d-flex align-items-center justify-content-center flex-shrink-0`}
                          style={{ width: 50, height: 50 }}
                        >
                          <i className={`fas fa-${meta.icon} fa-lg`} />
                        </div>
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <h5 className="mb-0 fw-bold">{notification.title}</h5>
                            <small className="text-muted">{formatDate(notification.createdAt)}</small>
                          </div>
                          <p className="mb-0 text-break">{notification.message}</p>
                        </div>
                        {!notification.isRead && (
                          <span className="badge bg-primary rounded-pill align-self-center ms-2">
                            Yeni
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="p-3">
                <SbEmptyState
                  iconClass="far fa-bell"
                  title="Bildirim Bulunmuyor"
                  description="Şu anda gösterilecek bildirim bulunmuyor."
                />
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
      {ratingTarget && (
        <RatingModal
          soldListingId={ratingTarget.soldListingId}
          listingTitle={ratingTarget.listingTitle}
          onSubmitted={() => markAsRead(ratingTarget.notificationId)}
          onClose={() => setRatingTarget(null)}
        />
      )}
    </div>
  );
};

export default Notifications;
